import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { UserCheck, Coffee, LogIn, LogOut, Building2, Home, Users, MoreHorizontal } from 'lucide-react';

function formatElapsed(since: string): string {
  const diff = Math.max(0, Date.now() - new Date(since).getTime());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const LOCATION_TYPES = ['office', 'home', 'client', 'other'] as const;
type LocationType = typeof LOCATION_TYPES[number];

const locationIcons: Record<LocationType, typeof Building2> = {
  office: Building2,
  home: Home,
  client: Users,
  other: MoreHorizontal,
};

export default function AttendanceButton() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [showMenu, setShowMenu] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [elapsed, setElapsed] = useState('00:00');
  const [error, setError] = useState('');

  const isAgentOrAdmin = user && (user.role === 'admin' || user.role === 'agent');

  const { data: statusData } = useQuery({
    queryKey: ['attendance-status'],
    queryFn: () => api.getAttendanceStatus(),
    enabled: !!isAgentOrAdmin,
    refetchInterval: 60000,
  });

  const status = statusData?.data;

  useEffect(() => {
    if (!status?.since || status.status === 'clocked_out') return;
    const tick = () => setElapsed(formatElapsed(status.since!));
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, [status?.since, status?.status]);

  const invalidateAttendance = () => {
    queryClient.invalidateQueries({ queryKey: ['attendance-status'] });
    queryClient.invalidateQueries({ queryKey: ['attendance'] });
  };

  const clockInMutation = useMutation({
    mutationFn: (locationType: LocationType) => api.clockIn(undefined, locationType),
    onSuccess: () => {
      invalidateAttendance();
      setShowMenu(false);
      setShowLocationPicker(false);
      setError('');
    },
    onError: (err: any) => setError(err.response?.data?.error?.message || t('attendance.clockIn') + ' failed'),
  });

  const clockOutMutation = useMutation({
    mutationFn: () => api.clockOut(),
    onSuccess: () => {
      invalidateAttendance();
      setShowMenu(false);
      setError('');
    },
    onError: (err: any) => setError(err.response?.data?.error?.message || t('attendance.clockOut') + ' failed'),
  });

  const breakStartMutation = useMutation({
    mutationFn: () => api.startBreak(),
    onSuccess: () => {
      invalidateAttendance();
      setShowMenu(false);
      setError('');
    },
    onError: (err: any) => setError(err.response?.data?.error?.message || t('attendance.breakStart') + ' failed'),
  });

  const breakEndMutation = useMutation({
    mutationFn: () => api.endBreak(),
    onSuccess: () => {
      invalidateAttendance();
      setShowMenu(false);
      setError('');
    },
    onError: (err: any) => setError(err.response?.data?.error?.message || t('attendance.breakEnd') + ' failed'),
  });

  if (!isAgentOrAdmin) return null;

  const statusColor = status?.status === 'clocked_in'
    ? 'bg-success-500'
    : status?.status === 'on_break'
    ? 'bg-warning-500'
    : 'bg-surface-400';

  const statusLabel = status?.status === 'clocked_in'
    ? t('attendance.clockedIn')
    : status?.status === 'on_break'
    ? t('attendance.onBreak')
    : t('attendance.clockedOut');

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-surface-100 transition-colors text-sm"
        title={statusLabel}
      >
        <div className="relative">
          <UserCheck size={18} className="text-surface-600" />
          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${statusColor}`} />
        </div>
        {status?.status !== 'clocked_out' && (
          <span className="text-xs font-mono text-surface-600 hidden sm:inline">{elapsed}</span>
        )}
      </button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setShowMenu(false); setShowLocationPicker(false); }} />
          <div className="absolute top-full mt-2 end-0 z-50 bg-surface-0 rounded-xl shadow-float border border-surface-400/15 p-3 min-w-[200px]" dir="rtl">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-surface-400/10">
              <div className={`w-3 h-3 rounded-full ${statusColor}`} />
              <span className="text-sm font-medium text-surface-700">{statusLabel}</span>
              {status?.status !== 'clocked_out' && (
                <span className="text-xs text-surface-500 ms-auto font-mono">{elapsed}</span>
              )}
            </div>

            {status?.today_minutes != null && status.today_minutes > 0 && (
              <div className="text-xs text-surface-500 mb-2">
                {t('attendance.todayHours')}: {Math.floor(status.today_minutes / 60)}:{String(status.today_minutes % 60).padStart(2, '0')}
              </div>
            )}

            {error && (
              <div className="text-xs text-danger-600 bg-danger-50 rounded p-1.5 mb-2">{error}</div>
            )}

            <div className="space-y-1">
              {status?.status === 'clocked_out' && !showLocationPicker && (
                <button
                  onClick={() => setShowLocationPicker(true)}
                  disabled={clockInMutation.isPending}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-success-50 text-success-700 transition-colors"
                >
                  <LogIn size={16} />
                  {t('attendance.clockIn')}
                </button>
              )}

              {status?.status === 'clocked_out' && showLocationPicker && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-surface-500 px-1">{t('attendance.selectLocation')}</div>
                  {LOCATION_TYPES.map((loc) => {
                    const Icon = locationIcons[loc];
                    return (
                      <button
                        key={loc}
                        onClick={() => clockInMutation.mutate(loc)}
                        disabled={clockInMutation.isPending}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-success-50 text-success-700 transition-colors"
                      >
                        <Icon size={16} />
                        {t(`attendance.location_${loc}`)}
                      </button>
                    );
                  })}
                </div>
              )}

              {status?.status === 'clocked_in' && (
                <>
                  <button
                    onClick={() => breakStartMutation.mutate()}
                    disabled={breakStartMutation.isPending}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-warning-50 text-warning-700 transition-colors"
                  >
                    <Coffee size={16} />
                    {t('attendance.breakStart')}
                  </button>
                  <button
                    onClick={() => clockOutMutation.mutate()}
                    disabled={clockOutMutation.isPending}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-danger-50 text-danger-700 transition-colors"
                  >
                    <LogOut size={16} />
                    {t('attendance.clockOut')}
                  </button>
                </>
              )}

              {status?.status === 'on_break' && (
                <>
                  <button
                    onClick={() => breakEndMutation.mutate()}
                    disabled={breakEndMutation.isPending}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-success-50 text-success-700 transition-colors"
                  >
                    <Coffee size={16} />
                    {t('attendance.breakEnd')}
                  </button>
                  <button
                    onClick={() => clockOutMutation.mutate()}
                    disabled={clockOutMutation.isPending}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-danger-50 text-danger-700 transition-colors"
                  >
                    <LogOut size={16} />
                    {t('attendance.clockOut')}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
